import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import {
  Card,
  CardBody,
  Container,
  Table,
  Input,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  Form,
} from "reactstrap";
import { allowOnlyNumber, urlEndPoint } from "helpers/GeneralHelper";
import { toast } from "react-toastify";
import {
  buildCallAuditFilesFormData,
  getCallAuditForEdit,
  submitCallAuditFiles,
  updateCallAudit,
} from "model/CallAuditModel";
import Loaders from "components/Common/Loaders";

const newMediaRow = () => ({
  key: `m-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
  file: null,
  notes: "",
  existingFileId: "",
  existingFileDisplayName: "",
});

const isoToDateInput = (iso) => {
  if (!iso) return "";
  const s = String(iso);
  const d = s.length >= 10 ? s.slice(0, 10) : "";
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : "";
};

const dateInputToIsoStart = (yyyyMmDd) => {
  if (!yyyyMmDd || !String(yyyyMmDd).trim()) return null;
  const d = new Date(`${String(yyyyMmDd).trim()}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/** Match API date samples (same midnight convention as auditDateFrom). */
const dateInputToIsoEnd = (yyyyMmDd) => dateInputToIsoStart(yyyyMmDd);

const yyyyMmDdToDdMmYyyy = (ymd) => {
  const p = String(ymd || "").trim();
  const m = p.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return p || "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const secondsToMmSs = (secs) => {
  const n = Math.max(0, Math.floor(Number(secs) || 0));
  const min = Math.floor(n / 60);
  const sec = n % 60;
  return `${min}:${String(sec).padStart(2, "0")}`;
};

/** MM:SS (minutes:seconds) → total seconds. */
const parseMmSsToSeconds = (raw) => {
  const t = String(raw ?? "").trim();
  if (!t) return 0;
  const parts = t.split(":");
  if (parts.length === 1) {
    const v = parseInt(parts[0], 10);
    return Number.isFinite(v) ? v : 0;
  }
  const min = parseInt(parts[0], 10) || 0;
  const secPart = parts[1] ?? "";
  const sec = secPart === "" ? 0 : Math.min(59, parseInt(secPart, 10) || 0);
  return min * 60 + sec;
};

const normalizeDetailRow = (d = {}) => ({
  ...d,
  score: d.score === 0 ? "0" : d.score != null ? String(d.score) : "",
  comments: d.comments ?? "",
  isDisqualified: !!d.isDisqualified,
  isDisqualifiedType: !!d.isDisqualifiedType,
  parameterTitle: d.parameterTitle ?? "",
  legendTitle: d.legendTitle ?? "",
  weightagePercentage: Number(d.weightagePercentage) || 0,
});

/** Preserve API order within each legend group (first-seen group order). */
const groupAuditItemsByLegend = (items = []) => {
  const groups = [];
  const indexByLegend = new Map();

  items.forEach((item) => {
    const legend = String(item.legendTitle || "").trim() || "Others";
    if (!indexByLegend.has(legend)) {
      indexByLegend.set(legend, groups.length);
      groups.push({ legendTitle: legend, items: [] });
    }
    groups[indexByLegend.get(legend)].items.push(item);
  });

  return groups;
};

const DisqualifiedYesNoToggle = ({ isYes, disabled, onChange }) => (
  <div className="audit-yes-no-toggle">
    <button
      type="button"
      className={`audit-yes-no-btn audit-yes-no-btn--yes${isYes ? " active" : ""} me-1`}
      disabled={disabled}
      onClick={() => onChange(true)}
    >
      YES
    </button>
    <button
      type="button"
      className={`audit-yes-no-btn audit-yes-no-btn--no${!isYes ? " active" : ""}`}
      disabled={disabled}
      onClick={() => onChange(false)}
    >
      NO
    </button>
  </div>
);

DisqualifiedYesNoToggle.propTypes = {
  isYes: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const resolveFileDisplayName = (f = {}) => {
  const name = f.fileName ?? f.name ?? f.displayName ?? f.originalFileName ?? "";
  if (name) return String(name);
  const url = String(f.fileDownloadUrl ?? f.url ?? "").trim();
  if (url) {
    const last = url.split("?")[0].split("/").filter(Boolean).pop();
    if (last) return last;
    return url;
  }
  if (f.uploadId) return `Upload ${f.uploadId}`;
  if (f.fileType) return `${f.fileType} file`;
  return "";
};

const extractMediaRowsFromPayload = (data = {}) => {
  const raw = data.files ?? data.attachments ?? data.mediaFiles ?? data.auditFiles ?? [];
  if (!Array.isArray(raw) || !raw.length) {
    return [newMediaRow()];
  }
  return raw.map((f, idx) => ({
    key: `api-${f.id ?? f.fileId ?? idx}-${idx}`,
    file: null,
    notes: f.notes ?? f.note ?? "",
    existingFileId: String(f.id ?? f.fileId ?? f.existingFileId ?? "").trim(),
    existingFileDisplayName: resolveFileDisplayName(f),
    fileDownloadUrl: f.fileDownloadUrl ?? f.url ?? "",
    fileType: f.fileType ?? "",
  }));
};

/** Rows worth sending on POST …/files (skip completely empty placeholders). */
const mediaRowsForUpload = (rows = []) =>
  rows.filter(
    (r) =>
      r?.file instanceof File ||
      String(r?.existingFileId ?? "").trim() ||
      String(r?.notes ?? "").trim()
  );

const extractFilesFromPayload = (data = {}) => {
  const raw = data.files ?? data.attachments ?? data.mediaFiles ?? data.auditFiles ?? [];
  return Array.isArray(raw) ? raw : [];
};

const buildFilesForPut = (auditFiles = [], mediaRows = []) => {
  const notesByFileId = new Map(
    mediaRows
      .filter((r) => String(r?.existingFileId ?? "").trim())
      .map((r) => [String(r.existingFileId).trim(), r.notes ?? ""])
  );

  return auditFiles.map((f) => {
    const id = String(f.id ?? f.fileId ?? "").trim();
    return {
      id,
      uploadId: f.uploadId ?? "",
      fileType: f.fileType ?? "",
      notes: notesByFileId.has(id) ? notesByFileId.get(id) : (f.notes ?? f.note ?? ""),
      fileName: f.fileName ?? f.name ?? f.displayName ?? f.originalFileName ?? null,
      updatedOn: f.updatedOn ?? "",
      fileDownloadUrl: f.fileDownloadUrl ?? f.url ?? "",
    };
  });
};

const buildPutPayload = ({
  snapshot,
  display,
  auditDateFromStr,
  auditDateToStr,
  isPublished,
  isAcknowledged,
  hasZeroToleranceViolation,
  callDuration,
  callDurationText,
  totalScoreComputed,
  auditSummary,
  auditItems,
  auditFiles,
  mediaRows,
}) => ({
  candidateClientId: snapshot.candidateClientId,
  recruiterId: snapshot.recruiterId,
  auditorId: snapshot.auditorId,
  auditDateFrom: dateInputToIsoStart(auditDateFromStr),
  auditDateTo: dateInputToIsoEnd(auditDateToStr),
  isPublished,
  isAcknowledged,
  hasZeroToleranceViolation,
  callDuration: Number(callDuration) || 0,
  callDurationTime: String(callDurationText ?? "").trim() || secondsToMmSs(callDuration),
  totalScore: totalScoreComputed,
  updatedOn: snapshot.updatedOn || null,
  recruiterDisplayName: display.recruiterDisplayName ?? "",
  candidateDisplayName: display.candidateDisplayName ?? "",
  candidatePhone: display.candidatePhone ?? "",
  jobName: display.jobName ?? "",
  clientDisplayName: display.clientDisplayName ?? "",
  auditorDisplayName: display.auditorDisplayName ?? "",
  auditSummary: auditSummary.trim() ? auditSummary.trim() : null,
  details: auditItems.map((d) => ({
    id: d.id,
    auditingParameterId: d.auditingParameterId,
    weightagePercentage: Number(d.weightagePercentage) || 0,
    score: parseFloat(String(d.score).trim()) || 0,
    comments: d.comments ?? "",
    isDisqualified: !!d.isDisqualified,
    parameterTitle: d.parameterTitle ?? "",
    legendTitle: d.legendTitle ?? "",
    isDisqualifiedType: !!d.isDisqualifiedType,
  })),
  files: buildFilesForPut(auditFiles, mediaRows),
});

const findScoreExceedingWeightage = (items = []) =>
  items.find((item) => {
    if (item.isDisqualifiedType) return false;
    const max = Number(item.weightagePercentage) || 0;
    const parsed = parseFloat(String(item.score ?? "").trim());
    return Number.isFinite(parsed) && parsed > max;
  });

const isFailureResponse = (res) =>
  res == null ||
  res.success === false ||
  String(res?.status ?? "").toLowerCase() === "error";

const isSuccessLike = (res) => !isFailureResponse(res);

const AuditEditForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location?.pathname;
  const { auditId } = useParams();

  const isViewMode = path.includes("view-call-audit");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /** IDs / snapshot for PUT body */
  const [snapshot, setSnapshot] = useState({
    candidateClientId: "",
    recruiterId: "",
    auditorId: "",
    updatedOn: "",
  });

  const [auditDateFromStr, setAuditDateFromStr] = useState("");
  const [auditDateToStr, setAuditDateToStr] = useState("");
  const [auditItems, setAuditItems] = useState([]);
  const [auditSummary, setAuditSummary] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [hasZeroToleranceViolation, setHasZeroToleranceViolation] = useState(false);
  /** Stored in seconds in API / PUT; edited as MM:SS in the summary field. */
  const [callDuration, setCallDuration] = useState(0);
  const [callDurationText, setCallDurationText] = useState("0:00");
  const [totalScoreComputed, setTotalScoreComputed] = useState(0);
  const [auditFiles, setAuditFiles] = useState([]);
  const [mediaRows, setMediaRows] = useState([newMediaRow()]);

  /** Read-only summary header */
  const [display, setDisplay] = useState({
    recruiterDisplayName: "",
    candidateDisplayName: "",
    candidatePhone: "",
    jobName: "",
    clientDisplayName: "",
    auditorDisplayName: "",
  });

  useEffect(() => {
    const sum = auditItems.reduce((acc, item) => {
      if (item.isDisqualifiedType) return acc;
      const n = parseFloat(String(item.score ?? "").trim());
      return acc + (Number.isFinite(n) ? n : 0);
    }, 0);
    setTotalScoreComputed(sum);
  }, [auditItems]);

  const totalWeightage = useMemo(
    () =>
      auditItems.reduce((sum, item) => {
        if (item.isDisqualifiedType) return sum;
        return sum + (Number(item.weightagePercentage) || 0);
      }, 0),
    [auditItems]
  );

  const groupedAuditItems = useMemo(() => groupAuditItemsByLegend(auditItems), [auditItems]);

  const applyFetchData = useCallback((data = {}) => {
    setSnapshot({
      candidateClientId: data.candidateClientId ?? "",
      recruiterId: data.recruiterId ?? "",
      auditorId: data.auditorId ?? "",
      updatedOn: data.updatedOn ?? "",
    });
    setDisplay({
      recruiterDisplayName: data.recruiterDisplayName ?? "",
      candidateDisplayName: data.candidateDisplayName ?? "",
      candidatePhone: data.candidatePhone ?? "",
      jobName: data.jobName ?? "",
      clientDisplayName: data.clientDisplayName ?? "",
      auditorDisplayName: data.auditorDisplayName ?? "",
      hasZeroToleranceViolation: data?.hasZeroToleranceViolation
    });
    setAuditDateFromStr(isoToDateInput(data.auditDateFrom));
    setAuditDateToStr(isoToDateInput(data.auditDateTo));
    setAuditSummary(data.auditSummary ?? "");
    setIsPublished(!!data.isPublished);
    setIsAcknowledged(!!data.isAcknowledged);
    setHasZeroToleranceViolation(!!data.hasZeroToleranceViolation);
    const durSecs = Number(data.callDuration) || 0;
    setCallDuration(durSecs);
    setCallDurationText(
      String(data.callDurationTime ?? "").trim() || secondsToMmSs(durSecs)
    );
    const details = Array.isArray(data.details) ? data.details.map(normalizeDetailRow) : [];
    details.sort((a, b) => (Number(a.auditingParameterId) || 0) - (Number(b.auditingParameterId) || 0));
    setAuditItems(details);
    setAuditFiles(extractFilesFromPayload(data));
    setMediaRows(extractMediaRowsFromPayload(data));
  }, []);

  useEffect(() => {
    if (!auditId) {
      toast.error("Missing audit id");
      navigate(`/${urlEndPoint.callAudit}`);
    }
  }, [auditId, navigate]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!auditId) return;
      setLoading(true);
      try {
        const res = await getCallAuditForEdit(auditId);
        if (cancelled) return;
        const data = res?.data ?? {};
        if (isFailureResponse(res)) {
          toast.error(res?.message || "Unable to load audit");
        } else if (data && typeof data === "object" && Object.keys(data).length) {
          applyFetchData(data);
        } else {
          toast.error("No audit data returned");
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.message || "Unable to load audit");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [auditId, applyFetchData]);

  const handleScoreChange = (rowId, raw) => {
    if (isViewMode) return;
    const sanitized = allowOnlyNumber(String(raw ?? ""));
    setAuditItems((prev) =>
      prev.map((item) => (item.id === rowId ? { ...item, score: sanitized } : item))
    );
  };

  const handleCommentsChange = (rowId, newComments) => {
    if (isViewMode) return;
    setAuditItems((prev) =>
      prev.map((item) => (item.id === rowId ? { ...item, comments: newComments } : item))
    );
  };

  const handleDisqualifiedChange = (rowId, checked) => {
    if (isViewMode) return;
    setAuditItems((prev) =>
      prev.map((item) => (item.id === rowId ? { ...item, isDisqualified: checked } : item))
    );
  };

  const addMediaRow = () => {
    if (isViewMode) return;
    setMediaRows((prev) => [...prev, newMediaRow()]);
  };

  const updateMediaRow = (key, patch) => {
    if (isViewMode) return;
    setMediaRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const handleSave = async () => {
    if (isViewMode) return;
    const id = String(auditId ?? "").trim();
    if (!id) {
      toast.error("Audit ID is required");
      return;
    }

    const invalidScoreItem = findScoreExceedingWeightage(auditItems);
    if (invalidScoreItem) {
      const max = Number(invalidScoreItem.weightagePercentage) || 0;
      toast.error(
        `Score cannot exceed weightage (${max.toFixed(2)}%) for "${invalidScoreItem.parameterTitle}".`
      );
      return;
    }

    const normalizedDuration = parseMmSsToSeconds(callDurationText);
    setCallDuration(normalizedDuration);
    setCallDurationText(secondsToMmSs(normalizedDuration));

    const putPayload = buildPutPayload({
      snapshot,
      display,
      auditDateFromStr,
      auditDateToStr,
      isPublished,
      isAcknowledged,
      hasZeroToleranceViolation,
      callDuration: normalizedDuration,
      callDurationText: secondsToMmSs(normalizedDuration),
      totalScoreComputed,
      auditSummary,
      auditItems,
      auditFiles,
      mediaRows,
    });

    setSaving(true);
    try {
      const putRes = await updateCallAudit(id, putPayload);
      if (!isSuccessLike(putRes)) {
        toast.error(putRes?.message || putRes?.error?.message || "Save failed");
        return;
      }

      const uploads = mediaRowsForUpload(mediaRows);
      if (uploads.length) {
        const fd = buildCallAuditFilesFormData(uploads);
        const fileRes = await submitCallAuditFiles(id, fd);
        if (!isSuccessLike(fileRes)) {
          toast.error(fileRes?.message || fileRes?.error?.message || "Media upload failed");
          return;
        }
      }

      toast.success(putRes?.message || "Audit saved.");
      const refresh = await getCallAuditForEdit(id);
      const refreshed = refresh?.data ?? {};
      if (refreshed && typeof refreshed === "object") {
        applyFetchData(refreshed);
      }
    } catch (e) {
      toast.error(e?.message || "Unable to save");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/${urlEndPoint.callAudit}`);
  };

  /** Digits + one colon only (MM:SS); `allowOnlyNumber` removes `:`, so it is used for phone only. */
  const handleCallDurationChange = (e) => {
    if (isViewMode) return;
    let next = String(e.target.value || "").replace(/[^\d:]/g, "");
    const idx = next.indexOf(":");
    if (idx !== -1) {
      const rest = next.slice(idx + 1).replace(/:/g, "");
      next = `${next.slice(0, idx + 1)}${rest.slice(0, 5)}`;
    }
    setCallDurationText(next);
    setCallDuration(parseMmSsToSeconds(next));
  };

  const handleCallDurationBlur = () => {
    if (isViewMode) return;
    const parsed = parseMmSsToSeconds(callDurationText);
    setCallDuration(parsed);
    setCallDurationText(secondsToMmSs(parsed));
  };

  const auditPeriodDisplayText = () => {
    const a = yyyyMmDdToDdMmYyyy(auditDateFromStr);
    const b = yyyyMmDdToDdMmYyyy(auditDateToStr);
    if (!a && !b) return "—";
    return `${a || "—"} To ${b || "—"}`;
  };

  const candidatePhoneDisplay = allowOnlyNumber(String(display.candidatePhone ?? "")).replace(/\./g, "");

  if (!auditId) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="page-content call-audit-edit-page">
        <Container fluid>
          <Card>
            <CardBody>
              <Row className="align-items-center mb-2">
                <Col>
                  <h5 className="mb-0">Audit Summary</h5>
                </Col>
                <Col className="text-end">
                  <span className={`${isPublished ? 'draft-span-success' : 'draft-span'}`}>
                  {isPublished ? <i className="bx bx-check-circle text-16" />: <i className="bx bx-time-five text-16" />}{" "}
                    {isPublished ? "PUBLISHED" : "DRAFT"}
                  </span>
                </Col>
              </Row>

              {loading ? (
                <Loaders />
              ) : (
                <>
                  <Row className="mt-3">
                    <Col md={4} className="mb-3">
                      <div className="font-weight-600">Recruiter Name</div>
                      <div className="mt-1">{display.recruiterDisplayName || "—"}</div>
                    </Col>
                    <Col md={4} className="mb-3">
                      <div className="font-weight-600">Candidate Name</div>
                      <div className="mt-1">
                        {display.candidateDisplayName ? (
                          snapshot.candidateClientId ? (
                            <Link
                              to={`/${urlEndPoint.callDetails}/${snapshot.candidateClientId}`}
                              className="text-primary text-decoration-underline"
                            >
                              {display.candidateDisplayName}
                            </Link>
                          ) : (
                            display.candidateDisplayName
                          )
                        ) : (
                          "—"
                        )}
                      </div>
                    </Col>
                    <Col md={4} className="mb-3">
                      <div className="font-weight-600">Candidate Phone</div>
                      <div className="mt-1">{candidatePhoneDisplay || "—"}</div>
                    </Col>
                  </Row>
                  <Row className="mt-md-0">
                    <Col md={4} className="mb-3">
                      <div className="font-weight-600">Job Name</div>
                      <div className="mt-1">{display.jobName || "—"}</div>
                    </Col>
                    <Col md={4} className="mb-3">
                      <div className="font-weight-600">Client Name</div>
                      <div className="mt-1">{display.clientDisplayName || "—"}</div>
                    </Col>
                    <Col md={4} className="mb-3">
                      <div className="font-weight-600">Auditor Name</div>
                      <div className="mt-1">{display.auditorDisplayName || "—"}</div>
                    </Col>
                  </Row>
                  <Row className="mt-md-0">
                    <Col md={4} className="mb-3">
                      <div className="font-weight-600">Audit Period</div>
                      <div className="mt-1">{auditPeriodDisplayText()}</div>
                      {/* {!isViewMode ? (
                        <Row className="g-2 gx-2 mt-2">
                          <Col xs={6}>
                            <Input
                              type="date"
                              value={auditDateFromStr}
                              onChange={(e) => setAuditDateFromStr(e.target.value)}
                              className="form-control-sm"
                            />
                          </Col>
                          <Col xs={6}>
                            <Input
                              type="date"
                              value={auditDateToStr}
                              onChange={(e) => setAuditDateToStr(e.target.value)}
                              className="form-control-sm"
                            />
                          </Col>
                        </Row>
                      ) : null} */}
                    </Col>
                    <Col md={4} className="mb-3">
                      <div className="font-weight-600">Total Score</div>
                      <div className="mt-1">{`${Number(totalScoreComputed).toFixed(2)} %`}</div>
                    </Col>
                    <Col md={4} className="mb-3">
                      <div className="font-weight-600">Call duration</div>
                      <div className="mt-1">
                        {isViewMode ? (
                          secondsToMmSs(callDuration)
                        ) : (
                          <>
                            <Input
                              type="text"
                              inputMode="numeric"
                              placeholder="0:00"
                              value={callDurationText}
                              onChange={handleCallDurationChange}
                              onBlur={handleCallDurationBlur}
                              className="form-control"
                              style={{ maxWidth: "120px" }}
                              autoComplete="off"
                            />
                            <small className="text-muted d-block mt-1">
                              Stored in seconds (e.g. 11:30 = 11 min 30 sec).
                            </small>
                          </>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <Row className="mt-md-0">
                    <Col md={4} className="mb-3">
                      <div className="font-weight-600">Zero Tolerance Violation</div>
                      <div className="mt-1">{hasZeroToleranceViolation ? 'Yes' : 'No'}</div>
                      
                    </Col>
                  </Row>
                </>
              )}
            </CardBody>

            {!loading ? (
              <CardBody>
                <Col className="mb-2">
                  <h5 className="mt-0">Edit Audit Parameter</h5>
                </Col>
                <hr />
                <Form>
                  <Table bordered responsive>
                    <thead>
                      <tr className="parameter-bg">
                        <th className="width-40">Parameter</th>
                        <th className="width-12 text-center">Weightage (%)</th>
                        <th className="width-13 text-center">Score (%)</th>
                        {/* <th className="width-10 text-center">Disqualified</th> */}
                        <th className="width-35">Comments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedAuditItems.map((group) => (
                        <React.Fragment key={group.legendTitle}>
                          <tr className="section-band">
                            <td colSpan={4}>{group.legendTitle}</td>
                          </tr>
                          {group.items.map((item) => (
                            <tr key={item.id ?? item.auditingParameterId}>
                              <td>{item.parameterTitle || item.parameter_title || "—"}</td>
                              <td className="text-center">
                                {Number(item.weightagePercentage ?? 0).toFixed(2)}
                              </td>
                              <td className="text-center">
                                {item.isDisqualifiedType ? (
                                  <DisqualifiedYesNoToggle
                                    isYes={!!item.isDisqualified}
                                    disabled={isViewMode}
                                    onChange={(checked) => handleDisqualifiedChange(item.id, checked)}
                                  />
                                ) : (
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    disabled={isViewMode}
                                    value={item.score}
                                    onChange={(e) => handleScoreChange(item.id, e.target.value)}
                                    placeholder="0.00"
                                  />
                                )}
                              </td>
                              <td>
                                <Input
                                  type="textarea"
                                  value={item.comments}
                                  disabled={isViewMode}
                                  onChange={(e) => handleCommentsChange(item.id, e.target.value)}
                                  rows={1}
                                />
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}

                      <tr className="font-weight-600 background-f4f6f9">
                        <td className="text-end">Total</td>
                        <td className="text-center">{totalWeightage.toFixed(2)} %</td>
                        <td className="text-center">
                          <Input
                            type="text"
                            readOnly
                            value={`${Number(totalScoreComputed).toFixed(2)} %`}
                            className="text-center bg-white"
                          />
                        </td>
                        <td />
                      </tr>
                    </tbody>
                  </Table>

                  <Row className="mt-3">
                    <Col md={12}>
                      <Label for="auditSummary" className="font-weight-600">
                        Final Audit Comments
                      </Label>
                      <Input
                        id="auditSummary"
                        type="textarea"
                        rows={4}
                        disabled={isViewMode}
                        value={auditSummary}
                        onChange={(e) => setAuditSummary(e.target.value)}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col md={12}>
                      <h6 className="fw-bold text-dark mb-3">Upload Media</h6>
                      {mediaRows.map((row) => (
                        <Row key={row.key} className="align-items-start g-2 mb-3">
                          <Col xs={12} md={5}>
                            <Label className="small text-muted d-block mb-1">File</Label>
                            <Input
                              type="file"
                              disabled={isViewMode}
                              onChange={(e) => {
                                const f = e.target.files?.[0] ?? null;
                                updateMediaRow(row.key, { file: f });
                              }}
                              className="form-control"
                            />
                            {row.fileDownloadUrl ? (
                              <div className="small mt-1">
                                <a
                                  href={row.fileDownloadUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary text-decoration-underline d-inline-flex align-items-center"
                                >
                                  <i className="bx bx-download me-1" aria-hidden="true" />
                                  Download file
                                </a>
                              </div>
                            ) : row.existingFileDisplayName ? (
                              <div className="small text-muted mt-1">
                                Current: {row.existingFileDisplayName}
                              </div>
                            ) : row.existingFileId ? (
                              <div className="small text-muted mt-1">Current file id: {row.existingFileId}</div>
                            ) : null}
                          </Col>
                          <Col xs={12} md={7}>
                            <Label className="small text-muted d-block mb-1">Notes</Label>
                            <Input
                              type="text"
                              placeholder="Enter notes (optional)"
                              disabled={isViewMode}
                              value={row.notes}
                              onChange={(e) => updateMediaRow(row.key, { notes: e.target.value })}
                            />
                          </Col>
                        </Row>
                      ))}
                      {!isViewMode ? (
                        <div className="d-flex justify-content-end">
                          <Button color="primary" outline type="button" onClick={addMediaRow}>
                            + Add More
                          </Button>
                        </div>
                      ) : null}
                    </Col>
                  </Row>

                  <Row className="align-items-center mt-3">
                    <Col md={6}>
                      <FormGroup check className="mb-2">
                        <Label check>
                          <Input
                            key={`published-${isPublished ? "1" : "0"}`}
                            type="checkbox"
                            disabled={isViewMode}
                            defaultChecked={isPublished}
                            onChange={(e) => setIsPublished(e.target.checked)}
                          />{" "}
                          Mark as Published
                        </Label>
                      </FormGroup>
                      <FormGroup check className="mb-2">
                        <Label check>
                          <Input
                            key={`ack-${isAcknowledged ? "1" : "0"}`}
                            type="checkbox"
                            disabled={isViewMode}
                            defaultChecked={isAcknowledged}
                            onChange={(e) => setIsAcknowledged(e.target.checked)}
                          />{" "}
                          Acknowledged
                        </Label>
                      </FormGroup>
                      <FormGroup check>
                        <Label check>
                          <Input
                            key={`ztv-${hasZeroToleranceViolation ? "1" : "0"}`}
                            type="checkbox"
                            disabled={isViewMode}
                            defaultChecked={hasZeroToleranceViolation}
                            onChange={(e) => setHasZeroToleranceViolation(e.target.checked)}
                          />{" "}
                          Zero tolerance violation
                        </Label>
                      </FormGroup>
                    </Col>

                    <Col md={6} className="text-end">
                      <Button color="secondary" onClick={handleCancel} className="me-2" disabled={saving}>
                        Back To Call Audit
                      </Button>
                      <Button color="primary" type="button" onClick={handleSave} disabled={isViewMode || saving}>
                        {saving ? "Saving…" : "Save"}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            ) : null}
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

AuditEditForm.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(AuditEditForm);
